import { Error, NotInterested, Store, Warning } from '@mui/icons-material'
import { I18n } from 'common'
import { InventoryDisplay, ProductLocationModel } from 'interfaces'
import { ReactElement } from 'react'
import WarningLabel from 'src/features/WarningLabel'
import { LocationType } from 'src/helpers/locationType'
import { defaultTheme } from 'src/theme'
import styled from 'styled-components'
import { getLocationLinkLabelKey } from './getLocationLinkLabelKey'

interface ProductCardDesktopAvailabilityProps {
  priceSpinner: boolean
  isCondensedView: boolean
  invDisplayState: InventoryDisplay
  activeLocation?: ProductLocationModel
  qtyAvailable?: number
  locType?: string
  called?: string
  isPrimary: boolean
  inStockSomewhere: boolean
  isZeroCost: boolean
  contactNumber: string
  getNonPrimaryLocationsQtyAvailable: number
  getPrimaryLocationQtyAvailable: { qty: number; called: string }
  getOtherLocationsQtyAvailable: number
  isMultiLocationEnabled: boolean
  showLocationSelector: () => void
  skeleton: ReactElement
}

export const ProductCardDesktopAvailability = ({
  priceSpinner,
  isCondensedView,
  invDisplayState,
  activeLocation,
  qtyAvailable,
  locType,
  called,
  isPrimary,
  inStockSomewhere,
  isZeroCost,
  contactNumber,
  getNonPrimaryLocationsQtyAvailable,
  getPrimaryLocationQtyAvailable,
  getOtherLocationsQtyAvailable,
  isMultiLocationEnabled,
  showLocationSelector,
  skeleton,
}: ProductCardDesktopAvailabilityProps): ReactElement => {
  if (priceSpinner) return skeleton

  return (
    <Cell style={{ alignItems: 'start' }}>
      {invDisplayState === InventoryDisplay.AVAILABLE_AT_LOCATION && (
        <Wrap className="fw-bold" $isCondensedView={isCondensedView}>
          {activeLocation?.locType === LocationType.VIEW_ONLY && (
            <StyledWarning theme={defaultTheme} />
          )}
          {qtyAvailable}&nbsp;
          <I18n name="at" />
          &nbsp;
          {locType === 'primary' && (
            <Store htmlColor={defaultTheme.colors.green} />
          )}
          {called}
          {isPrimary && (
            <SubLocationQtyInfo>
              <I18n
                name="atOtherLocations"
                textReplacement={[getNonPrimaryLocationsQtyAvailable]}
              />
            </SubLocationQtyInfo>
          )}
          {!isPrimary && (
            <SubLocationQtyInfo>
              {getPrimaryLocationQtyAvailable.qty}&nbsp;
              <I18n name="at" />
              &nbsp;
              <Store htmlColor={defaultTheme.colors.greyDark} />
              {getPrimaryLocationQtyAvailable.called}
            </SubLocationQtyInfo>
          )}
        </Wrap>
      )}
      {invDisplayState === InventoryDisplay.SHOW_ZERO && (
        <Wrap className="fw-bold" $isCondensedView={isCondensedView}>
          <ZeroStockAvailability
            callForAvailability={!inStockSomewhere && isZeroCost}
            called={called}
            isPrimary={isPrimary}
            getNonPrimaryLocationsQtyAvailable={
              getNonPrimaryLocationsQtyAvailable
            }
            getPrimaryLocationQtyAvailable={getPrimaryLocationQtyAvailable}
          />
        </Wrap>
      )}
      {invDisplayState === InventoryDisplay.CALL_FOR_AVAILABILITY && (
        <>
          <Wrap className="fw-bold">
            <Error htmlColor={defaultTheme.colors.primary} />
            &nbsp;
            <I18n name="callForAvailability" />
          </Wrap>
          <Description>
            <I18n name="itemUnavailableAtAllLocations" />
            <br />
            {contactNumber && (
              <I18n
                name="pleaseContactForInformation"
                textReplacement={[contactNumber]}
              />
            )}
          </Description>
        </>
      )}
      {activeLocation?.locType === LocationType.VIEW_ONLY && (
        <I18n name="viewOnlyLocationMessage" style={{ width: '150px' }} />
      )}
      {activeLocation?.locType === LocationType.ORDER_FROM_PRIMARY && (
        <WarningLabel
          name="orderFromPrimaryWarining"
          style={{ width: '150px' }}
        />
      )}
      {inStockSomewhere && (
        <Wrap $isCondensedView={isCondensedView}>
          <LocalLink
            onClick={showLocationSelector}
            style={{
              ...(isCondensedView && { marginTop: '4px' }),
              whiteSpace: 'nowrap',
            }}
          >
            {!isPrimary && (
              <SubLocationQtyInfo>
                <I18n
                  name="atOtherLocations"
                  textReplacement={[getOtherLocationsQtyAvailable]}
                />
              </SubLocationQtyInfo>
            )}
            {isPrimary && (
              <I18n name={getLocationLinkLabelKey(isMultiLocationEnabled)} />
            )}
          </LocalLink>
        </Wrap>
      )}
    </Cell>
  )
}

const ZeroStockAvailability = ({
  callForAvailability,
  called,
  isPrimary,
  getNonPrimaryLocationsQtyAvailable,
  getPrimaryLocationQtyAvailable,
}: {
  callForAvailability: boolean
  called?: string
  isPrimary: boolean
  getNonPrimaryLocationsQtyAvailable: number
  getPrimaryLocationQtyAvailable: { qty: number; called: string }
}): ReactElement => {
  if (callForAvailability) {
    return (
      <>
        <NotInterested htmlColor={defaultTheme.colors.red} />
        <I18n name="callForAvailability" />
      </>
    )
  }

  return (
    <>
      <I18n name="zeroAvailableAtLocation" textReplacement={[called]} />
      {isPrimary && (
        <SubLocationQtyInfo>
          <I18n
            name="atOtherLocations"
            textReplacement={[getNonPrimaryLocationsQtyAvailable]}
          />
        </SubLocationQtyInfo>
      )}
      {!isPrimary && (
        <SubLocationQtyInfo>
          {getPrimaryLocationQtyAvailable.qty}&nbsp;
          <I18n name="at" />
          &nbsp;
          <Store htmlColor={defaultTheme.colors.greyDark} />
          {getPrimaryLocationQtyAvailable.called}
        </SubLocationQtyInfo>
      )}
    </>
  )
}

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  font-size: 16px;
  line-height: 24px;
  text-align: start;
`

interface CondensedProps {
  $isCondensedView?: boolean
}

const Wrap = styled.span<CondensedProps>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: ${(p) => (p.$isCondensedView ? '14px' : '16px')};
`

const LocalLink = styled.div`
  color: ${defaultTheme.colors.link};
  text-decoration: underline;
  cursor: pointer;
`

const Description = styled.div`
  font-size: 14px;
  line-height: 20px;
  margin-top: 4px;
`

const StyledWarning = styled(Warning)`
  color: ${defaultTheme.colors.warningBackground};
  margin-right: 4px;
`

const SubLocationQtyInfo = styled.div`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  font-family: Roboto;
`
